import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import { getPackages } from '../services/assuranceService';

export default function Step5() {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const packages = await getPackages();
        const packagesList = Array.isArray(packages) ? packages : packages.data || [];
        setData({data: packagesList});
      } catch (error) {
        console.error('Erreur lors de la récupération des packages :', error);
      }
    };
    fetchPackages();

    // Récupérer le package sauvegardé si existant
    const savedPackage = localStorage.getItem('selectedPackage');
    if (savedPackage) {
      setSelectedPackage(savedPackage);
    }
  }, []);

  const handlePackageChange = (key) => {
    setSelectedPackage(key);
    localStorage.setItem('selectedPackage', key);
    if (errors.package) {
      setErrors(prev => ({ ...prev, package: '' }));
    }
  };

  const handleContinue = (e) => {
    e.preventDefault();
    if (!selectedPackage) {
      setErrors({ package: 'Veuillez sélectionner une formule.' });
      return;
    }
    setErrors({});
    localStorage.setItem('selectedPackage', selectedPackage);
    navigate('/step-6');
  };

  return (
    <div className="subscribe py-10 lg:py-16 bg-[#e4eef9]">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-center text-4xl font-bold">Formulaire de souscription</h1>
        <div className="mt-10">
          <ProgressBar currentStep={5} />
          <div className="mt-8 bg-white p-6 lg:p-10 rounded-xl shadow-sm">
            <h2 className="text-xl font-bold text-secondary mb-6">5. Quelle formule d'assurance souhaitez-vous ?</h2>
            <form className="space-y-4">
              <div>
                <label className="block font-bold mb-4">Sélectionnez la formule qui vous convient :</label>
                <div className="flex flex-wrap gap-3">
                  {data && data.data && data.data.length > 0 ? data.data.map((pkg) => (
                    <label 
                      key={pkg.key} 
                      className={`border-2 px-6 py-3 rounded-full cursor-pointer transition ${selectedPackage === pkg.key ? 'bg-secondary text-white border-secondary' : 'border-gray-800 hover:bg-gray-100'}`}
                    >
                      <input 
                        type="radio" 
                        name="formule" 
                        className="sr-only" 
                        checked={selectedPackage === pkg.key} 
                        onChange={() => handlePackageChange(pkg.key)} 
                      />
                      {pkg.name}
                    </label>
                  )) : (
                    <p>Chargement des formules...</p>
                  )}
                </div>
                {errors.package && <p className="text-red-500 text-sm mt-2">{errors.package}</p>}
              </div>
            </form>
            
            {selectedPackage && data && data.data && (
              <div className="mt-6 p-6 bg-blue-50 rounded-lg">
                <h3 className="font-bold text-lg mb-6 text-secondary">Détails de la formule sélectionnée</h3>
                {(() => {
                  const selectedPkg = data.data.find(pkg => pkg.key === selectedPackage);
                  if (!selectedPkg || !selectedPkg.details) return <p>Aucun détail disponible</p>;

                  // Fonction pour rendre une valeur selon son type
                  const renderValue = (item, idx) => {
                    // Si c'est un objet avec title et value (ex: plafonds)
                    if (typeof item === 'object' && item !== null && item.title !== undefined && item.value !== undefined) {
                      return (
                        <li key={idx} className="flex justify-between items-center">
                          <span className="text-gray-700">{item.title}:</span>
                          <span className="font-bold text-primary text-sm">
                            {typeof item.value === 'number' 
                              ? `${new Intl.NumberFormat('fr-FR').format(item.value)} F CFA`
                              : item.value}
                          </span>
                        </li>
                      );
                    }
                    // Si c'est une chaîne simple (ex: garanties, primes)
                    if (typeof item === 'string') {
                      return (
                        <li key={idx} className="flex items-center text-gray-700">
                          <span className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></span>
                          <span className='text-sm'>{item}</span>
                        </li>
                      );
                    }
                    // Si c'est un nombre
                    if (typeof item === 'number') {
                      return (
                        <li key={idx} className="flex items-center text-gray-700">
                          <span className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></span>
                          <span className="font-bold text-primary text-sm">
                            {new Intl.NumberFormat('fr-FR').format(item)} F CFA
                          </span>
                        </li>
                      );
                    }
                    return null;
                  };

                  // Boucler dynamiquement sur toutes les sections de details
                  const detailSections = Object.entries(selectedPkg.details);

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {detailSections.map(([sectionKey, section]) => {
                        if (!section) return null;

                        return (
                          <div key={sectionKey} className="bg-white rounded-lg p-4 border-l-4 border-primary">
                            <h4 className="font-bold text-primary mb-4 text-base">
                              {section.title || sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)}
                            </h4>
                            <ul className="space-y-2">
                              {Array.isArray(section.value) ? (
                                section.value.map((item, idx) => renderValue(item, idx))
                              ) : section.value !== undefined ? (
                                renderValue(section.value, 0)
                              ) : (
                                <li className="text-gray-500">Aucune donnée</li>
                              )}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
            
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/step-4"
                className="rounded-full border border-gray-300 px-8 py-3 text-gray-700 font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition"
              >
                ← Retour
              </Link>
              <button
                onClick={handleContinue}
                className="rounded-full bg-primary px-8 py-3 text-secondary font-medium flex items-center justify-center gap-2 hover:bg-primary-dark transition"
              >
                Continuer →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
